import type { CompanyLabelPrintingSettings, LabelSizeId } from "../types";
import { ALL_LABEL_SIZES, DEFAULT_LABEL_SIZE } from "../zpl/sizes";

export const LABEL_PRINTING_SETTING_KEY = "label_printing";

/**
 * Default company policy. `enabled: true` so the platform env alone is enough
 * to enable Zebra printing until an admin explicitly saves a policy.
 */
export const DEFAULT_COMPANY_LABEL_PRINTING: CompanyLabelPrintingSettings = {
  enabled: true,
  defaultLabelSize: DEFAULT_LABEL_SIZE,
  qzCertificate: "",
};

export function isLabelSizeId(value: unknown): value is LabelSizeId {
  return typeof value === "string" && (ALL_LABEL_SIZES as readonly string[]).includes(value);
}

function coerceLabelSize(value: unknown): LabelSizeId {
  return isLabelSizeId(value) ? value : DEFAULT_LABEL_SIZE;
}

function coerceString(value: unknown): string {
  return typeof value === "string" ? value : "";
}

function coerceBool(value: unknown, fallback: boolean): boolean {
  if (typeof value === "boolean") return value;
  if (typeof value === "string") {
    const v = value.toLowerCase();
    if (v === "true" || v === "1") return true;
    if (v === "false" || v === "0") return false;
  }
  return fallback;
}

/**
 * Parse whatever comes back from `company_settings.setting_value` into a strict
 * CompanyLabelPrintingSettings shape. Accepts a raw JSON string, an object, or
 * null/undefined. Never throws.
 */
export function parseCompanyLabelPrintingSettings(raw: unknown): CompanyLabelPrintingSettings {
  let value: unknown = raw;
  if (typeof value === "string") {
    try {
      value = JSON.parse(value);
    } catch {
      return { ...DEFAULT_COMPANY_LABEL_PRINTING };
    }
  }
  if (!value || typeof value !== "object") {
    return { ...DEFAULT_COMPANY_LABEL_PRINTING };
  }
  const obj = value as Record<string, unknown>;
  return {
    enabled: coerceBool(obj.enabled, DEFAULT_COMPANY_LABEL_PRINTING.enabled),
    defaultLabelSize: coerceLabelSize(obj.defaultLabelSize),
    qzCertificate: coerceString(obj.qzCertificate),
  };
}
